import {
  BOT_PLAN_LABELS,
  BOT_PLANS,
  BOT_STYLE_LABELS,
  BOT_STYLES,
  type BotModelInfo,
  type BotModelsDto,
  type BotPlan,
  type BotSettingsDto,
  type BotStyle,
  type BotTreatment,
  type BotUsageDto,
} from '@performance-crm/shared';
import { useEffect, useState, type FormEvent } from 'react';
import { BotEvaluation } from '@/components/bot/BotEvaluation';
import { BotSimulator } from '@/components/bot/BotSimulator';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast-context';
import {
  useBotModels,
  useBotSettings,
  useBotUsage,
  useUpdateBotSettings,
  useWhatsappAccount,
  useWhatsappTemplates,
} from '@/lib/queries';

/** Cerebro del bot (ADR-020): personalidad, plan y precios, modelos, uso del mes y simulador. */
export function BotTab({ companyId }: { companyId: string }) {
  const { data, isLoading } = useBotSettings(companyId);
  const { data: models } = useBotModels(companyId);
  const { data: usage } = useBotUsage(companyId);
  if (isLoading || !data) return <Skeleton className="h-64 w-full" />;
  return (
    <div className="space-y-4">
      <SettingsForm key={JSON.stringify(data.bot)} companyId={companyId} data={data} />
      <div className="grid gap-4 lg:grid-cols-2">
        <ModelsCard models={models} />
        <UsageCard usage={usage} />
      </div>
      <BotSimulator companyId={companyId} defaultPlan={data.bot.plan} />
      <BotEvaluation companyId={companyId} />
    </div>
  );
}

const split = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

function SettingsForm({ companyId, data }: { companyId: string; data: BotSettingsDto }) {
  const update = useUpdateBotSettings();
  const toast = useToast();
  const { data: account } = useWhatsappAccount(companyId);
  const { data: templates } = useWhatsappTemplates(companyId);
  const b = data.bot;
  const p = b.persona;
  const [f, setF] = useState({
    active: b.active,
    firstContactTemplateId: b.firstContactTemplateId ?? '',
    name: p.name,
    treatment: p.treatment as BotTreatment,
    style: p.style as BotStyle,
    greeting: p.greeting ?? '',
    farewell: p.farewell ?? '',
    doSay: p.doSay.join(', '),
    dontSay: p.dontSay.join(', '),
    qualification: p.topics.qualification ?? '',
    pricing: p.topics.pricing ?? '',
    appointments: p.topics.appointments ?? '',
    objections: p.topics.objections ?? '',
    plan: b.plan as BotPlan,
    priceMode: b.priceMode,
    monthlyBudgetUsd: String(b.monthlyBudgetUsd),
    onBudgetExceeded: b.onBudgetExceeded,
    maxTurns: String(b.maxTurns),
    appointmentMinutes: String(b.appointmentMinutes),
    disclose: b.disclose,
  });
  const set = (patch: Partial<typeof f>) => setF((x) => ({ ...x, ...patch }));
  useEffect(() => {
    /* la clave del componente cambia con los datos guardados: el formulario se reinicia solo */
  }, []);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await update.mutateAsync({
        companyId,
        bot: {
          persona: {
            name: f.name.trim(),
            treatment: f.treatment,
            style: f.style,
            greeting: f.greeting.trim() || null,
            farewell: f.farewell.trim() || null,
            doSay: split(f.doSay),
            dontSay: split(f.dontSay),
            topics: {
              qualification: f.qualification.trim() || null,
              pricing: f.pricing.trim() || null,
              appointments: f.appointments.trim() || null,
              objections: f.objections.trim() || null,
            },
          },
          plan: f.plan,
          priceMode: f.priceMode,
          monthlyBudgetUsd: Number(f.monthlyBudgetUsd) || 0,
          onBudgetExceeded: f.onBudgetExceeded,
          maxTurns: Number(f.maxTurns) || 12,
          appointmentMinutes: Number(f.appointmentMinutes) || 60,
          disclose: f.disclose,
          active: f.active,
          firstContactTemplateId: f.firstContactTemplateId || null,
        },
      });
      toast.success('Bot guardado');
    } catch (err) {
      toast.error('No se pudo guardar', (err as Error).message);
    }
  };
  return (
    <form onSubmit={(e) => void submit(e)} className="grid gap-4 lg:grid-cols-2">
      <Card className="lg:col-span-2" data-testid="bot-live-card">
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            Conversar con clientes por WhatsApp
            <Badge tone={f.active ? 'success' : 'muted'} data-testid="bot-live-status">
              {f.active ? 'Encendido' : 'Apagado'}
            </Badge>
          </CardTitle>
          <CardDescription>
            Apagado, el bot solo existe en el simulador. Encendido, responde por el número propio de
            la empresa a los leads que atiende (proyectos con «el bot atiende primero») y escribe
            primero a los que están por contactar.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={f.active}
              onChange={(e) => set({ active: e.target.checked })}
              data-testid="bot-active"
            />
            El bot conversa con clientes reales por WhatsApp
          </label>
          <div className="space-y-1">
            <Label htmlFor="bot-first-template">Plantilla de primer contacto</Label>
            <Select
              id="bot-first-template"
              value={f.firstContactTemplateId}
              onChange={(e) => set({ firstContactTemplateId: e.target.value })}
              data-testid="bot-first-template"
            >
              <option value="">Sin plantilla (solo responde a quien escribe primero)</option>
              {templates
                ?.filter((t) => t.active)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
            <p className="text-muted-foreground text-xs">
              WhatsApp solo permite texto libre dentro de las 24 h desde el último mensaje del
              cliente: para escribir primero se usa esta plantilla.
            </p>
          </div>
          <p className="text-muted-foreground text-xs sm:col-span-2" data-testid="bot-live-hint">
            {account
              ? account.mode === 'SIMULADO'
                ? `Número ${account.phoneNumber} en modo simulado: los mensajes no salen al celular; pruébalo desde la ficha de un lead con «Simular cliente».`
                : `Número ${account.phoneNumber} en modo real: los mensajes del bot llegan al celular del cliente.`
              : 'La empresa aún no tiene número de WhatsApp (Administración → Número de WhatsApp): el bot no puede escribir.'}
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Personalidad</CardTitle>
          <CardDescription>
            Una sola personalidad por empresa: así se presenta y así habla ante el cliente. Las
            indicaciones por tema actúan por dentro.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="bot-name">Nombre del asistente</Label>
            <Input
              id="bot-name"
              value={f.name}
              onChange={(e) => set({ name: e.target.value })}
              maxLength={40}
              placeholder="VYVIA"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-treatment">Tratamiento</Label>
            <Select
              id="bot-treatment"
              value={f.treatment}
              onChange={(e) => set({ treatment: e.target.value as BotTreatment })}
            >
              <option value="USTED">De usted</option>
              <option value="TU">De tú</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-style">Estilo</Label>
            <Select
              id="bot-style"
              value={f.style}
              onChange={(e) => set({ style: e.target.value as BotStyle })}
            >
              {BOT_STYLES.map((s) => (
                <option key={s} value={s}>
                  {BOT_STYLE_LABELS[s]}
                </option>
              ))}
            </Select>
          </div>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={f.disclose}
              onChange={(e) => set({ disclose: e.target.checked })}
            />
            Se presenta como asistente virtual
          </label>
          <div className="space-y-1 sm:col-span-2">
            <Label htmlFor="bot-greeting">Saludo inicial (opcional)</Label>
            <Textarea
              id="bot-greeting"
              rows={2}
              value={f.greeting}
              onChange={(e) => set({ greeting: e.target.value })}
              placeholder="Vacío = «Hola, soy VYVIA, asistente virtual de …»"
              maxLength={300}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-dosay">Frases que sí usa (separadas por coma)</Label>
            <Input
              id="bot-dosay"
              value={f.doSay}
              onChange={(e) => set({ doSay: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-dontsay">Frases que nunca usa (separadas por coma)</Label>
            <Input
              id="bot-dontsay"
              value={f.dontSay}
              onChange={(e) => set({ dontSay: e.target.value })}
              placeholder="descuento, oferta limitada"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-farewell">Despedida (opcional)</Label>
            <Input
              id="bot-farewell"
              value={f.farewell}
              onChange={(e) => set({ farewell: e.target.value })}
              maxLength={200}
            />
          </div>
          <div className="sm:col-span-2">
            <p className="text-sm font-medium">Indicaciones por tema</p>
          </div>
          {(
            [
              ['qualification', 'Al calificar'],
              ['pricing', 'Sobre precios y financiamiento'],
              ['appointments', 'Sobre citas'],
              ['objections', 'Ante objeciones'],
            ] as const
          ).map(([key, label]) => (
            <div key={key} className="space-y-1">
              <Label htmlFor={`bot-${key}`}>{label}</Label>
              <Textarea
                id={`bot-${key}`}
                rows={2}
                value={f[key]}
                onChange={(e) => set({ [key]: e.target.value } as Partial<typeof f>)}
                maxLength={600}
              />
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Plan, precios y presupuesto</CardTitle>
          <CardDescription>
            El plan define qué modelos usa el bot; el presupuesto mensual es el tope de gasto en
            modelos. Los límites de mensajes y el silencio nocturno siguen en Empresa.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="bot-plan">Plan</Label>
            <Select
              id="bot-plan"
              value={f.plan}
              onChange={(e) => set({ plan: e.target.value as BotPlan })}
            >
              {BOT_PLANS.map((pl) => (
                <option key={pl} value={pl}>
                  {BOT_PLAN_LABELS[pl]}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-prices">Precios</Label>
            <Select
              id="bot-prices"
              value={f.priceMode}
              onChange={(e) => set({ priceMode: e.target.value as 'DESDE' | 'NO' })}
            >
              <option value="DESDE">Da precios «desde» por tipología</option>
              <option value="NO">No da precios: deriva a la cita</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-budget">Presupuesto mensual (USD)</Label>
            <Input
              id="bot-budget"
              type="number"
              min={0}
              step="1"
              value={f.monthlyBudgetUsd}
              onChange={(e) => set({ monthlyBudgetUsd: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-exceeded">Al llegar al presupuesto</Label>
            <Select
              id="bot-exceeded"
              value={f.onBudgetExceeded}
              onChange={(e) => set({ onBudgetExceeded: e.target.value as 'BASICO' | 'PAUSAR' })}
            >
              <option value="BASICO">Sigue en plan Básico (sin costo)</option>
              <option value="PAUSAR">Se pausa y avisa</option>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-turns">Turnos máximos por conversación</Label>
            <Input
              id="bot-turns"
              type="number"
              min={2}
              max={50}
              value={f.maxTurns}
              onChange={(e) => set({ maxTurns: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bot-minutes">Duración de la cita (minutos)</Label>
            <Input
              id="bot-minutes"
              type="number"
              min={15}
              max={240}
              step={15}
              value={f.appointmentMinutes}
              onChange={(e) => set({ appointmentMinutes: e.target.value })}
            />
          </div>
          <div className="sm:col-span-2">
            <Button type="submit" disabled={update.isPending} data-testid="bot-save">
              Guardar
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
}

function ModelLine({ label, m }: { label: string; m: BotModelInfo }) {
  return (
    <li className="flex flex-wrap items-center gap-1">
      <span className="text-muted-foreground w-16">{label}</span>
      <code className="text-xs">{m.requested ?? 'reglas y plantillas'}</code>
      {m.fallbackToFake && <Badge tone="warning">simulado: sin clave</Badge>}
    </li>
  );
}

function ModelsCard({ models }: { models: BotModelsDto | undefined }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Modelos por plan</CardTitle>
        <CardDescription>
          Se configuran en el servidor. Sin clave del proveedor, el bot entiende por reglas y
          responde con plantillas (no cuesta nada).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm" data-testid="bot-models">
        {models ? (
          <>
            {BOT_PLANS.map((p) => (
              <div key={p}>
                <p className="font-medium">{BOT_PLAN_LABELS[p]}</p>
                <ul className="mt-1 space-y-0.5">
                  <ModelLine label="entender" m={models.plans[p].understand} />
                  <ModelLine label="redactar" m={models.plans[p].reply} />
                </ul>
              </div>
            ))}
            <p className="text-muted-foreground text-xs">
              Proveedores:{' '}
              {models.providers
                .map((p) => `${p.id} (${p.ready ? 'listo' : 'sin clave'})`)
                .join(' · ')}
            </p>
          </>
        ) : (
          <Skeleton className="h-24 w-full" />
        )}
      </CardContent>
    </Card>
  );
}

function UsageCard({ usage }: { usage: BotUsageDto | undefined }) {
  const pct =
    usage && usage.budgetUsd > 0
      ? Math.min(100, Math.round((usage.real.costUsd / usage.budgetUsd) * 100))
      : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Uso del mes</CardTitle>
        <CardDescription>
          Turnos reales (con clientes) y de prueba (simulador), con su costo en modelos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm" data-testid="bot-usage">
        {usage ? (
          <>
            <div>
              <div className="flex justify-between">
                <span>
                  Real: {usage.real.runs} turno{usage.real.runs === 1 ? '' : 's'} · USD{' '}
                  {usage.real.costUsd.toFixed(2)}
                </span>
                <span className="text-muted-foreground">
                  presupuesto USD {usage.budgetUsd} ({pct}%)
                </span>
              </div>
              <div className="bg-muted mt-1 h-2 w-full rounded">
                <div
                  className={`h-2 rounded ${pct >= 80 ? 'bg-destructive' : 'bg-primary'}`}
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              Simulador: {usage.simulated.runs} turno{usage.simulated.runs === 1 ? '' : 's'} · USD{' '}
              {usage.simulated.costUsd.toFixed(4)} · tokens reales:{' '}
              {usage.real.input + usage.real.cachedInput} de entrada ({usage.real.cachedInput} en
              caché), {usage.real.output} de salida
            </p>
            {usage.byModel.length > 0 && (
              <ul className="space-y-0.5">
                {usage.byModel.map((m) => (
                  <li key={m.model} className="flex justify-between">
                    <code className="text-xs">{m.model}</code>
                    <span>
                      {m.runs} · USD {m.costUsd.toFixed(4)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          <Skeleton className="h-24 w-full" />
        )}
      </CardContent>
    </Card>
  );
}
